import { useCallback, useMemo } from "react";
import type { RowData } from "@tanstack/react-table";
import { isEqual } from "@common/helpers/comparison";
import type { View } from "@common/types/views/views";
import { getLeafColumnInfos, isSameColumnState } from "../columns/columnLayout";
import type { DataGridColumn } from "../columns/types";
import type { DataGridColumnState, DataGridDensity } from "../types";
import { columnStateToViewColumns } from "./columnStateToViewColumns";
import {
    DENSITY_BY_VIEW_DENSITY,
    VIEW_DENSITY_BY_DENSITY,
} from "./savedViewDensity";
import { useStableColumnState } from "./useStableColumnState";
import { viewColumnsToColumnState } from "./viewColumnsToColumnState";

interface UseSavedViewStateInput<TView extends View, TData extends RowData> {
    view: TView;
    /** Receives the view with new `columns`; other fields are kept. */
    onViewChange: (view: TView) => void;
    /** The table's columns, to tell hidden columns from unknown ones. */
    columns: ReadonlyArray<DataGridColumn<TData>>;
    /**
     * The view Reset goes back to. Column settings compare against its
     * columns, hidden columns keep their place in its order, and Reset
     * restores its density when it sets one. Without it the definitions are
     * the default.
     */
    defaultView?: View;
}

/**
 * Stores the table's column order, widths, visibility, pins, and variants
 * (`customVariants`) in a saved view's `columns`, and its density in
 * `options.density`, in the shape
 * ViewTable reads and writes. A view without a density leaves the table's. Spread the
 * result into `useDataGrid`; the table itself never sees the view. Only
 * real changes are written: an echo of the state read writes nothing, and
 * columns this table doesn't render keep their place and options.
 */
export const useSavedViewState = <TView extends View, TData extends RowData>({
    view,
    onViewChange,
    columns,
    defaultView,
}: UseSavedViewStateInput<TView, TData>) => {
    const leaves = useMemo(() => getLeafColumnInfos(columns), [columns]);
    const defaultViewColumns = defaultView?.columns;
    const viewDefault = useMemo(
        () =>
            defaultViewColumns &&
            viewColumnsToColumnState({
                viewColumns: defaultViewColumns,
                leaves,
            }),
        [defaultViewColumns, leaves],
    );
    const defaultColumnState = useStableColumnState(viewDefault);
    const columnState = useStableColumnState(
        useMemo(
            () =>
                viewColumnsToColumnState({
                    viewColumns: view.columns,
                    leaves,
                    referenceOrder: defaultColumnState?.order,
                    fallback: defaultColumnState,
                }),
            [view.columns, leaves, defaultColumnState],
        ),
    );
    const onColumnStateChange = useCallback(
        (state: DataGridColumnState) => {
            if (isSameColumnState(state, columnState)) {
                return;
            }
            const nextColumns = columnStateToViewColumns({
                state,
                viewColumns: view.columns,
                leaves,
            });
            if (!isEqual(nextColumns, view.columns)) {
                onViewChange({ ...view, columns: nextColumns });
            }
        },
        [view, onViewChange, leaves, columnState],
    );
    const onDensityChange = useCallback(
        (density: DataGridDensity) => {
            const viewDensity = VIEW_DENSITY_BY_DENSITY[density];
            if (viewDensity !== view.options?.density) {
                onViewChange({
                    ...view,
                    options: { ...view.options, density: viewDensity },
                });
            }
        },
        [view, onViewChange],
    );
    const viewDensity = view.options?.density;
    const defaultViewDensity = defaultView?.options?.density;
    return {
        columnState,
        onColumnStateChange,
        defaultColumnState,
        onDensityChange,
        ...(viewDensity && { density: DENSITY_BY_VIEW_DENSITY[viewDensity] }),
        ...(defaultViewDensity && {
            defaultDensity: DENSITY_BY_VIEW_DENSITY[defaultViewDensity],
        }),
    };
};
